import postService from '../services/posts';
import { createVoteThunks } from './voteThunks';
import { createReactionThunk } from './reactionThunks';
import type { AppDispatch } from '../store';
import type { PostItem, PostsState, ReduxAction, VotePayload } from '../types/common';

type PostReducerAction = ReduxAction<PostsState | VotePayload | string>;

const postReducer = (
  state: PostsState = { loading: false, error: null, results: [] },
  action: PostReducerAction
): PostsState => {
  switch (action.type) {
    case 'SET_POSTS_LOADING':
      return { ...state, loading: true, error: null };
    case 'SET_POSTS_ERROR':
      return { ...state, loading: false, error: action.payload as string };
    case 'SET_POSTS':
      return { ...(action.payload as PostsState), loading: false, error: null };
    case 'LOAD_MORE_POSTS':
      return {
        ...(action.payload as PostsState),
        loading: false,
        error: null,
        results: [...state.results, ...((action.payload as PostsState).results || [])],
      };
    case 'ADD_NEW_POST': {
      const newPost = action.payload as unknown as PostItem;
      // Avoid duplicates if a refresh already pulled it in.
      if (state.results.some((r) => r.id === newPost.id)) return state;
      return { ...state, results: [newPost, ...state.results] };
    }
    case 'TOGGLE_VOTE':
    case 'TOGGLE_REACTION':
      return {
        ...state,
        results: state.results.map((r: PostItem) =>
          r.id !== (action.payload as VotePayload).id
            ? r
            : { ...r, ...((action.payload as VotePayload).data || {}) }
        ),
      };
    // Hiding removes the card from the feed immediately; the server has it in
    // hiddenPosts, so it stays gone on the next fetch too.
    case 'DELETE_POST':
    case 'HIDE_POST':
      return {
        ...state,
        results: state.results.filter((r: PostItem) => r.id !== (action.payload as string)),
      };
    default:
      return state;
  }
};

export const fetchPosts = (signal?: AbortSignal) => {
  return async (dispatch: AppDispatch) => {
    if (!signal || signal.aborted) return;

    dispatch({ type: 'SET_POSTS_LOADING' });

    try {
      const posts = await postService.getPosts(10, 1, signal);

      if (!signal.aborted) {
        dispatch({
          type: 'SET_POSTS',
          payload: posts,
        });
      }
    } catch (error: unknown) {
      if (!signal.aborted) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to load posts';
        dispatch({
          type: 'SET_POSTS_ERROR',
          payload: errorMessage,
        });
        throw error;
      }
    }
  };
};

/**
 * Silently re-fetch the first page of the feed and replace the list, without
 * toggling the loading spinner. Used for periodic (web) and pull-to-refresh
 * (mobile) refreshes so the view doesn't flash a full-page loader.
 */
export const refreshPosts = () => {
  return async (dispatch: AppDispatch) => {
    const posts = await postService.getPosts(10, 1);
    dispatch({ type: 'SET_POSTS', payload: posts });
  };
};

export const loadMorePosts = (page: number) => {
  return async (dispatch: AppDispatch) => {
    const posts = await postService.getPosts(10, page);
    dispatch({
      type: 'LOAD_MORE_POSTS',
      payload: posts,
    });
  };
};

/**
 * Hide a post. Optimistic: the card goes immediately and is restored if the
 * request fails, because a card that lingers after you hid it reads as broken.
 * HIDE_POST is handled by both the home and explore slices.
 */
export const hidePost = (id: string) => {
  return async (dispatch: AppDispatch, getState: () => any) => {
    const snapshot = getState().posts?.results ?? [];
    const exploreSnapshot = getState().explore?.results ?? [];
    dispatch({ type: 'HIDE_POST', payload: id });
    try {
      await postService.hidePost(id);
    } catch (err) {
      dispatch({ type: 'SET_POSTS', payload: { ...getState().posts, results: snapshot } });
      dispatch({ type: 'SET_EXPLORE_POSTS', payload: { ...getState().explore, results: exploreSnapshot } });
      throw err;
    }
  };
};

/** Report a post; the server hides it too, so the card goes the same way. */
export const reportPost = (id: string, reason: string, detail?: string) => {
  return async (dispatch: AppDispatch) => {
    await postService.reportPost(id, reason, detail);
    dispatch({ type: 'HIDE_POST', payload: id });
  };
};

export const { toggleUpvote, toggleDownvote } = createVoteThunks('TOGGLE_VOTE');
export const toggleReaction = createReactionThunk('TOGGLE_REACTION');

export const removePost = (id: string) => {
  return async (dispatch: AppDispatch) => {
    await postService.deletePost(id);

    dispatch({
      type: 'DELETE_POST',
      payload: id,
    });
  };
};

export default postReducer;
